'use client'

import { useState } from 'react'
import Link from 'next/link'

import { Target } from 'lucide-react'

import { Card } from '@/components/ui/card'
import SectionHero from '@/components/SectionHero'
import AccuracyLegend from '@/components/AccuracyLegend'
import MetricExplainer from '@/components/MetricExplainer'
import { topicShortName } from '@/lib/sat/topic-short-names'
import type { LeverageItem } from '@/lib/scoring/leverage-board'
import { FULL_CONFIDENCE_CUTOFF } from '@/lib/scoring/topic-leverage'
import type { TopicPerformance } from './page'

/**
 * Practice, as a console: two columns, everything on one screen.
 *
 * Chosen over the alternative (one big action, the rest behind pills) because
 * Practice is where a student decides WHAT to work on, and that decision needs
 * the topic list visible — hiding it behind a tab turns a choice into a
 * navigation problem.
 *
 * Left: the ways to start. Right: what to start ON, ranked by what it is worth.
 */

export const MODES = [
  {
    href: '/drills/mini',
    label: 'Daily drill',
    detail: '12 mixed · resets midnight',
    icon: '⚡',
    pro: false,
    wide: false,
  },
  {
    href: '/rapid-fire',
    label: 'Rapid fire',
    // Was "beat the clock" under a stopwatch icon. Rapid Fire has no clock —
    // its only timers are the short auto-advances after an answer (see
    // cause-drills.ts, which already refused to offer it as a timed mode for
    // exactly that reason). It is a streak mode, so say that.
    detail: 'build a streak',
    icon: '🔥',
    pro: true,
    wide: false,
  },
  // Spans the row. Three tiles in a two-column grid left this one orphaned
  // beside a gap — and it is not a third session type anyway, it is the door
  // to the whole topic list, so the width is honest as well as tidier.
  {
    href: '/practice/topics',
    label: 'By topic',
    detail: 'you choose',
    icon: '⌗',
    pro: false,
    wide: true,
  },
] as const

/**
 * Difficulty is a SETTING on a drill, not a kind of practice.
 *
 * It used to be a "mode" tile that navigated to /practice/adaptive, which
 * redirects to /practice?mode=adaptive — a round trip back to this page, since
 * the console does not read that param. So the tile appeared to do nothing.
 *
 * The session has always supported ?difficulty= and defaults to ADAPTIVE. This
 * exposes that choice at the moment it applies: pick it here and every Drill
 * link below starts that way. Adaptive stays the default because letting the
 * engine follow you is the right answer unless you have a reason.
 */
export const DIFFICULTIES = [
  { key: 'ADAPTIVE', label: 'Adaptive', hint: 'follows your level' },
  { key: 'E', label: 'Easier', hint: 'build confidence' },
  { key: 'M', label: 'Medium', hint: 'steady' },
  { key: 'H', label: 'Harder', hint: 'push' },
] as const

export type DifficultyKey = (typeof DIFFICULTIES)[number]['key']

/**
 * Carry the page's difficulty setting onto a session href that was built
 * elsewhere (the leverage engine's section fallback). The hint below the
 * control says it "applies to every drill you start from this page"; the
 * fallback rows used to drop it, so "Harder" opened an Adaptive section run.
 */
export function withDrillDifficulty(href: string, difficulty: DifficultyKey): string {
  const [path, query = ''] = href.split('?')
  const params = new URLSearchParams(query)
  params.set('difficulty', difficulty)
  return `${path}?${params.toString()}`
}

/** Accuracy → colour. Four bands, so a glance sorts topics without reading. */
export function accuracyTone(pct: number): { bar: string; text: string } {
  if (pct < 65) return { bar: 'bg-red-500', text: 'text-red-600 dark:text-red-400' }
  if (pct < 75) return { bar: 'bg-orange-500', text: 'text-orange-600 dark:text-orange-400' }
  if (pct < 85) return { bar: 'bg-yellow-500', text: 'text-yellow-600 dark:text-yellow-500' }
  return { bar: 'bg-emerald-600', text: 'text-emerald-700 dark:text-emerald-400' }
}

/** Weakest first, and only topics actually attempted — an untouched topic has
 *  no accuracy to rank, and showing it at 0% would read as a failure. */
export function rankWeakest(topics: TopicPerformance[]): (TopicPerformance & { pct: number })[] {
  return topics
    .filter((t) => t.total > 0)
    .map((t) => ({ ...t, pct: Math.round((t.correct / t.total) * 100) }))
    .sort((a, b) => a.pct - b.pct || b.total - a.total)
}

/** One row of the ranked column, from either ranking. */
export type PracticeRow = {
  topic: string
  pct: number
  /** Expected questions per test, when the leverage engine supplied it. */
  questionsPerTest: number | null
  /**
   * This topic's leverage as a share of the top row's, 0–1.
   *
   * The ranking is gap x questionsPerTest x confidence, and this column showed
   * only the first two — both ROUNDED — so two rows reading "~7/test" might be
   * 6.6 and 7.4, and a list whose order cannot be derived from what it displays
   * reads as arbitrary. The original LeverageBoard carried a relative-impact
   * bar; compacting it into a list dropped that. This puts it back.
   */
  impact: number | null
  /** Questions answered in this topic — the confidence input, below. */
  answered: number
  href: string
}

/**
 * Under FULL_CONFIDENCE_CUTOFF answers, leverage is damped (down to a half at
 * LOW_CONFIDENCE_CUTOFF) because the accuracy is not yet trustworthy. That is
 * the honest reason a very weak, rarely-tested topic can rank low — and it was
 * invisible, so the row looked mis-sorted rather than unproven.
 */
export function lowConfidenceNote(answered: number): string | null {
  if (answered <= 0 || answered >= FULL_CONFIDENCE_CUTOFF) return null
  return `only ${answered} answered`
}

/**
 * The right column's ranking.
 *
 * This column used to sort by raw accuracy — "weakest first". That is the
 * weaker question: a 58% topic worth one question a test matters less than a
 * 65% topic worth eight, and drilling the 58% first is how a student spends an
 * afternoon to gain nothing. The leverage engine already answers the better
 * question (gap x frequency), and its board was sitting on /study-plan showing
 * the ranking NEXT TO the plan that ranking had already produced. So it moves
 * here, where the student is choosing what to drill, and the accuracy-only
 * list it duplicated goes away.
 *
 * Falls back to weakest-first when leverage has nothing to say — a student with
 * no test yet has no frequency data, and an empty column is worse than a
 * rougher one.
 */
export function buildPracticeRows(
  leverage: LeverageItem[],
  topics: TopicPerformance[],
  drillHref: (topic: string) => string,
  limit = 12,
  /** Applied to the engine's own fallback href (a topic with no bank
   *  questions routes to its section) — so the page's difficulty setting
   *  reaches those rows too. */
  fallbackHref: (href: string) => string = (href) => href,
): { rows: PracticeRow[]; ranked: boolean } {
  if (leverage.length > 0) {
    // Relative to the TOP row, so the bar answers "how much does this one
    // matter next to the best one" rather than exposing a raw score.
    const top = Math.max(...leverage.map((l) => l.leverage), 0)
    return {
      ranked: true,
      rows: leverage.slice(0, limit).map((l) => ({
        topic: l.topic,
        pct: Math.round(l.pct),
        questionsPerTest: l.questionsPerTest,
        impact: top > 0 ? l.leverage / top : null,
        answered: l.total,
        // The engine's own href already routes non-bank topics to a section
        // fallback, but it carries its own difficulty default — the control on
        // this page has to win for routable topics.
        href: l.routable ? drillHref(l.topic) : fallbackHref(l.href),
      })),
    }
  }
  return {
    ranked: false,
    rows: rankWeakest(topics)
      .slice(0, limit)
      .map((t) => ({
        topic: t.topic,
        pct: t.pct,
        questionsPerTest: null,
        // The fallback ranking is accuracy alone — there is no leverage to
        // show a share of, so no bar rather than a fabricated one.
        impact: null,
        answered: t.total,
        href: drillHref(t.topic),
      })),
  }
}

export type RecentDrill = {
  id: string
  /** What it was on: "Boundaries", "Math", "Daily Drill", a drill's title. */
  title: string
  /** How it ran: "Hard · 10 questions". Shown under the title when present. */
  detail?: string | null
  /** The whole description in one line, for the tooltip. */
  summary?: string
  score: number
  outOf: number
  completedAt: string
  /** Where the row goes, or null when there is nowhere useful. */
  href: string | null
  /**
   * 'sitting' opens that session's questions and answers; 'review' a drill's
   * missed questions; 'practice' starts the topic again.
   */
  hrefKind?: 'sitting' | 'review' | 'practice'
  /** Secondary "practise this topic again" link, shown beside the row. */
  againHref?: string | null
}

/** The link's tooltip — it must say which thing the link does. */
export function recentLinkTitle(d: RecentDrill): string {
  if (d.hrefKind === 'sitting') return `Review this session's questions — ${d.summary ?? d.title}`
  return d.hrefKind === 'practice'
    ? `${d.summary ?? d.title} — practice this topic again`
    : `Review your answers — ${d.title}`
}

/** The footnote under the list: only the link kinds actually on screen. */
export function recentLinksNote(rows: RecentDrill[]): string | null {
  const linked = rows.filter((r) => r.href)
  const sitting = linked.some((r) => r.hrefKind === 'sitting')
  const review = linked.some((r) => r.hrefKind === 'review' || r.hrefKind === undefined)
  const practice = linked.some((r) => r.hrefKind === 'practice')
  if (sitting) {
    const again = rows.some((r) => r.againHref)
    return review
      ? 'Open a session to see its questions and your answers; drill rows open the questions you missed.'
      : again
        ? 'Open a session to see its questions and your answers, or practise its topic again.'
        : 'Open a session to see its questions and your answers.'
  }
  if (review && practice)
    return 'Drill rows open the questions you missed; topic rows start that topic again.'
  if (review) return 'Rows with a link open the questions you missed.'
  if (practice) return 'Rows with a link start that topic again.'
  return null
}

export default function PracticeConsole({
  topics,
  paid,
  bestRapidFireRun = null,
  recentDrills = [],
  leverage = [],
  leverageTotalGain = null,
  ceilingPct,
  goalScore = null,
  answeredThisWeek = 0,
  sessionsThisWeek = 0,
}: {
  topics: TopicPerformance[]
  paid: boolean
  /** Most correct in a finished rapid-fire run. Null = never finished one. */
  bestRapidFireRun?: number | null
  recentDrills?: RecentDrill[]
  /** Leverage-ranked topics from the same engine the study plan uses. */
  leverage?: LeverageItem[]
  /** Honest projected gain (400–1600) from bringing ALL shown topics to the
   *  target together — one board-level number, never a sum of row deltas. */
  leverageTotalGain?: number | null
  /**
   * The accuracy the ranking measures each topic against — the lowest whole
   * percent whose projected total reaches the student's goal, or 85% with no
   * goal set. It already drives `gap`, and so the ordering; showing it answers
   * "how good is good enough?", which a bare 72% cannot.
   */
  ceilingPct?: number
  /** The goal the target was scaled to. Null = none set, so say so. */
  goalScore?: number | null
  /** Questions answered in the last 7 days. */
  answeredThisWeek?: number
  /** Practice sittings finished in the last 7 days. */
  sessionsThisWeek?: number
}) {
  const answered = topics.reduce((n, t) => n + t.total, 0)
  const [difficulty, setDifficulty] = useState<DifficultyKey>('ADAPTIVE')
  const drillHref = (topic: string) =>
    `/practice/session?topic=${encodeURIComponent(topic)}&count=10&difficulty=${difficulty}`
  const { rows, ranked } = buildPracticeRows(leverage, topics, drillHref, 12, (href) =>
    withDrillDifficulty(href, difficulty),
  )
  const worked = topics.filter((t) => t.total > 0).length

  // A tile shows a number only when a real one exists. The alternative —
  // inventing a plausible-looking level or streak — is how a product starts
  // lying in small ways.
  const statFor: Record<string, { value: string; label: string } | null> = {
    '/rapid-fire':
      bestRapidFireRun !== null ? { value: String(bestRapidFireRun), label: 'best run' } : null,
    '/practice/topics': { value: String(topics.length), label: 'topics' },
  }

  return (
    <div className="mx-auto w-full max-w-[1080px] space-y-3 px-4 py-6 sm:px-6 sm:py-8">
      <SectionHero
        kind="practice"
        title="Practice"
        subtitle="Drill any topic at your own pace — as much as you like."
      >
        <span className="text-right text-[11.5px] text-emerald-700 dark:text-emerald-400">
          <span className="block">
            {topics.length} topics · {answered.toLocaleString()} answered
          </span>
          {/* Only when it ADDS something. Once every topic has been touched this
              line just repeats the count above it — reported as "30 topics ·
              705 answered / 30 worked on so far". */}
          {worked > 0 && worked < topics.length && (
            <span className="mt-0.5 block text-[11px] text-muted-foreground">
              {worked} of {topics.length} worked on so far
            </span>
          )}
        </span>
      </SectionHero>

      <div className="grid items-stretch gap-3 sm:grid-cols-[0.95fr_1.05fr]">
        <Card className="h-full p-3.5">
          <h2 className="mb-2 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
            Start drilling
          </h2>
          <div className="grid grid-cols-2 gap-2">
            {MODES.map((m) => (
              <Link
                key={m.href}
                href={m.href}
                className={`rounded-xl border border-emerald-200 bg-emerald-50 p-2.5 transition-colors hover:bg-emerald-100 dark:border-emerald-900 dark:bg-emerald-950/50 dark:hover:bg-emerald-950 ${m.wide ? 'col-span-2' : ''}`}
              >
                <span className="flex items-start justify-between">
                  <span aria-hidden="true" className="text-[15px]">
                    {m.icon}
                  </span>
                  {statFor[m.href] && (
                    <span className="text-right">
                      <span className="block text-[15px] font-semibold leading-none text-emerald-900 dark:text-emerald-100">
                        {statFor[m.href]!.value}
                      </span>
                      <span className="mt-0.5 block text-[9px] uppercase tracking-[0.06em] text-emerald-700 dark:text-emerald-400">
                        {statFor[m.href]!.label}
                      </span>
                    </span>
                  )}
                  {/* Badged HERE rather than on arrival: finding out a mode is
                      Pro after committing to its screen is a worse moment. */}
                  {m.pro && !paid && (
                    <span className="rounded-full bg-white px-1.5 py-px text-[9px] font-bold text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">
                      PRO
                    </span>
                  )}
                </span>
                <span className="mt-1 block text-[13px] font-semibold text-emerald-900 dark:text-emerald-100">
                  {m.label}
                </span>
                <span className="mt-px block text-[11px] text-emerald-700 dark:text-emerald-400">
                  {m.detail}
                </span>
              </Link>
            ))}
          </div>
          {/* The Method Coach (/practice/methods): a text link, not a tile —
              it is a Math-only experiment, not a way to drill. */}
          <Link
            href="/practice/methods"
            className="mt-2 flex items-center justify-between rounded-lg px-1 py-1.5 text-[11.5px] font-semibold text-emerald-700 hover:underline dark:text-emerald-400"
          >
            <span>Method coach: compare how you solve →</span>
            {!paid && (
              <span className="rounded-full bg-emerald-50 px-1.5 py-px text-[9px] font-bold text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">
                PRO
              </span>
            )}
          </Link>
          {/* MOVED here from the topic column's header. Difficulty is a setting
              on the drill you are about to start, so it belongs with the ways
              to start one — and the column it left was carrying a heading, a
              gain line, a target and four pills in one row. */}
          <div className="mt-3.5 border-t border-muted pt-3">
            <h2 className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
              Difficulty
            </h2>
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Drill difficulty">
              {DIFFICULTIES.map((d) => (
                <button
                  key={d.key}
                  type="button"
                  aria-pressed={difficulty === d.key}
                  title={d.hint}
                  onClick={() => setDifficulty(d.key)}
                  className={`rounded-lg px-2.5 py-1.5 text-[11.5px] font-semibold transition-colors ${
                    difficulty === d.key
                      ? 'bg-emerald-600 text-white'
                      : 'border border-border bg-card text-muted-foreground hover:bg-accent'
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-[10.5px] text-muted-foreground">
              {DIFFICULTIES.find((d) => d.key === difficulty)?.hint} — applies to every drill you
              start from this page.
            </p>
          </div>

          {/* Real activity, so the column has something to say before any
              history exists. Both numbers are counted, not estimated, and the
              block is omitted entirely when there is nothing to report rather
              than showing a row of zeroes. */}
          {(answeredThisWeek > 0 || sessionsThisWeek > 0) && (
            <div className="mt-3.5 border-t border-muted pt-3">
              <h2 className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                This week
              </h2>
              <div className="flex gap-6">
                <div>
                  <div className="text-[17px] font-semibold leading-none text-emerald-700 dark:text-emerald-400">
                    {answeredThisWeek}
                  </div>
                  <div className="mt-1 text-[10px] uppercase tracking-[0.07em] text-muted-foreground">
                    questions
                  </div>
                </div>
                {sessionsThisWeek > 0 && (
                  <div>
                    <div className="text-[17px] font-semibold leading-none text-emerald-700 dark:text-emerald-400">
                      {sessionsThisWeek}
                    </div>
                    <div className="mt-1 text-[10px] uppercase tracking-[0.07em] text-muted-foreground">
                      {sessionsThisWeek === 1 ? 'session' : 'sessions'}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {recentDrills.length > 0 && (
            <div className="mt-3.5 border-t border-muted pt-3">
              <h2 className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                Recent practice
              </h2>
              <ul>
                {recentDrills.map((d, i) => {
                  const row = (
                    <>
                      <span
                        className="min-w-0 flex-1"
                        // A linked row's tooltip is the link's, which says what it does.
                        title={d.href ? undefined : (d.summary ?? d.title)}
                      >
                        <span className="block truncate text-[12px]">{d.title}</span>
                        {d.detail && (
                          <span className="block truncate text-[10.5px] text-muted-foreground">
                            {d.detail}
                          </span>
                        )}
                      </span>
                      <span className="shrink-0 text-[11px] text-muted-foreground">
                        {new Date(d.completedAt).toLocaleDateString(undefined, {
                          day: 'numeric',
                          month: 'short',
                        })}
                      </span>
                      <span className="w-11 shrink-0 text-right text-[12px] font-semibold">
                        {d.outOf > 0 ? `${d.score}/${d.outOf}` : d.score}
                      </span>
                    </>
                  )
                  return (
                    <li
                      key={d.id}
                      className={
                        i < recentDrills.length - 1
                          ? 'flex items-center gap-1 border-b border-muted'
                          : 'flex items-center gap-1'
                      }
                    >
                      {d.href ? (
                        <Link
                          href={d.href}
                          className="-mx-1 flex min-w-0 flex-1 items-center gap-2 rounded px-1 py-1.5 transition-colors hover:bg-accent"
                          title={recentLinkTitle(d)}
                        >
                          {row}
                        </Link>
                      ) : (
                        <span className="flex min-w-0 flex-1 items-center gap-2 py-1.5">{row}</span>
                      )}
                      {d.againHref && (
                        // Beside the row, never inside it: a link in a link is
                        // invalid HTML. The row opens the review; this goes again.
                        <Link
                          href={d.againHref}
                          className="shrink-0 rounded px-1 py-1 text-[10.5px] font-medium text-brand-ink hover:underline"
                          title={`Practise ${d.title} again`}
                          aria-label={`Practise ${d.title} again`}
                        >
                          Again
                        </Link>
                      )}
                    </li>
                  )
                })}
              </ul>
              {recentLinksNote(recentDrills) && (
                <p className="mt-1.5 text-[10.5px] text-muted-foreground">
                  {recentLinksNote(recentDrills)}
                </p>
              )}
            </div>
          )}
        </Card>

        <Card className="h-full p-3.5">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              {/* KEEPS ITS NAME. This is the leverage board — the same engine,
                  merged into a compact column when it moved off the study plan.
                  Renaming it to "Worth the most points" made a feature the
                  student knew look like it had been deleted: asked twice where
                  the highest-leverage moves had gone, while looking at them.
                  The icon and the name are what carry the identity; the compact
                  list is what makes the page fit on one screen. */}
              <h2 className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                {ranked && (
                  <Target
                    className="size-3 shrink-0 text-emerald-700 dark:text-emerald-400"
                    aria-hidden="true"
                  />
                )}
                {ranked ? 'Your highest-leverage moves' : 'Your topics — weakest first'}
              </h2>
              {/* One board-level number, not a sum of per-row deltas — those
                  would over-credit and falsely add up. */}
              {ranked && (
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  {leverageTotalGain !== null && leverageTotalGain > 0 && (
                    <>
                      Up to{' '}
                      <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                        +{leverageTotalGain}
                      </span>{' '}
                      from these together
                    </>
                  )}
                  {ceilingPct !== undefined && (
                    <>
                      {leverageTotalGain !== null && leverageTotalGain > 0 ? ' · ' : ''}
                      aiming at <span className="font-semibold text-foreground">{ceilingPct}%</span>
                      {goalScore !== null ? (
                        ` for your ${goalScore} goal`
                      ) : (
                        <>
                          {' '}
                          —{' '}
                          <Link href="/settings/study" className="underline underline-offset-2">
                            set a goal
                          </Link>{' '}
                          to scale it
                        </>
                      )}
                    </>
                  )}
                </p>
              )}
            </div>
          </div>
          {rows.length === 0 ? (
            <p className="py-2 text-[12.5px] text-muted-foreground">
              Nothing answered yet. Any of the ways to start will fill this in.
            </p>
          ) : (
            <ul>
              {rows.map((r, i) => {
                const tone = accuracyTone(r.pct)
                const note = lowConfidenceNote(r.answered)
                return (
                  <li
                    key={r.topic}
                    className={`py-1.5 ${i < rows.length - 1 ? 'border-b border-muted' : ''}`}
                  >
                    <div className="flex items-center gap-2.5">
                      <Link
                        href={r.href}
                        className="min-w-0 flex-1 truncate text-[12.5px] hover:underline"
                        title={r.topic}
                      >
                        {topicShortName(r.topic)}
                      </Link>
                      {/* Frequency is the half of leverage accuracy cannot show —
                          without it the ordering looks arbitrary next to the %. */}
                      {r.questionsPerTest !== null && r.questionsPerTest >= 0.5 && (
                        <span className="shrink-0 text-[10.5px] tabular-nums text-muted-foreground">
                          ~{Math.round(r.questionsPerTest)}/test
                        </span>
                      )}
                      <span className="relative h-[5px] w-[60px] shrink-0 rounded-full bg-muted">
                        <span
                          className={`block h-full rounded-full ${tone.bar}`}
                          style={{ width: `${r.pct}%` }}
                        />
                        {/* Where "good enough" sits for THIS student's goal.
                            The distance from the fill to this tick is the gap
                            the ranking is built on, made visible. */}
                        {ranked && ceilingPct !== undefined && (
                          <span
                            aria-hidden="true"
                            className="absolute -top-[1.5px] h-[8px] w-[1.5px] rounded-full bg-foreground/45"
                            style={{ left: `calc(${Math.min(100, ceilingPct)}% - 0.75px)` }}
                          />
                        )}
                      </span>
                      <span className="w-8 shrink-0 text-right text-[11.5px] text-muted-foreground">
                        {r.pct}%
                      </span>
                    </div>

                    {/* The ordering, made visible. Without it the list is sorted
                        by a number the reader cannot see. */}
                    {r.impact !== null && (
                      <div className="mt-1 flex items-center gap-2">
                        <span
                          className="h-[3px] w-[92px] shrink-0 overflow-hidden rounded-full bg-muted"
                          aria-hidden="true"
                        >
                          <span
                            className="block h-full rounded-full bg-indigo-500/70"
                            style={{ width: `${Math.max(4, Math.round(r.impact * 100))}%` }}
                          />
                        </span>
                        {note && (
                          <span className="truncate text-[10px] text-muted-foreground">{note}</span>
                        )}
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
          {/* Ranked rows carry the leverage engine's RECENT accuracy; the
              weakest-first fallback and the all-topics list carry ALL-TIME
              accuracy. Name which, and say why they differ. */}
          {rows.length > 0 && <AccuracyLegend metric={ranked ? 'recent' : 'allTime'} />}
          {ranked && rows.some((r) => r.impact !== null) && (
            <p className="mt-1 text-[10.5px] text-muted-foreground">
              <span
                aria-hidden="true"
                className="mr-1.5 inline-block h-[3px] w-4 rounded-full bg-indigo-500/70 align-middle"
              />
              = points on offer, relative to the top topic. A topic you have barely answered ranks
              lower until there is enough to judge it on.
              {ceilingPct !== undefined && (
                <> The tick on each accuracy bar is your {ceilingPct}% target.</>
              )}
            </p>
          )}
          {ranked && rows.length > 0 && <MetricExplainer shows="recent" className="mt-1.5" />}
          <Link
            href="/practice/topics"
            className="mt-2 block text-[11.5px] font-semibold text-emerald-700 hover:underline dark:text-emerald-400"
          >
            See all {topics.length} topics →
          </Link>
        </Card>
      </div>
    </div>
  )
}
